/**
 * 연 약정 할인
 * ---------------------------------------------------------------------------
 * ## 두 가지를 한 화면에 둔다
 * 지금 걸려 있는 약정과, 아직 안 건 구독이다. 따로 두면 "우리 CRM 은
 * 약정했는데 ECO 는 왜 그대로지" 를 알 수 없다.
 *
 * ## 아끼는 금액보다 **되돌려 낼 금액**을 더 또렷이 적는다
 * 할인은 누르는 순간 좋아 보이고, 환수는 몇 달 뒤에 온다. 그때 "그런 말
 * 없었다" 가 되지 않으려면 누르기 전 화면에 숫자가 있어야 한다. 약정 중인
 * 카드에는 **지금 그만두면 얼마**인지를 늘 띄워 둔다.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App as AntdApp, Button, Card, Descriptions, Empty, Modal, Space, Tag, Typography } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Paragraph } = Typography;

interface CommitmentTerms {
  months: number;
  /** 0.2 = 20% */
  discountRate: number;
}

interface CommitmentView {
  commitmentId: string;
  subscriptionId: string;
  productCode: string;
  productName: string;
  planName: string | null;
  months: number;
  discountRate: number;
  startedOn: string;
  endsOn: string;
  status: string;
  clawbackIfBrokenNow: number;
  savedSoFar: number;
}

interface CommitmentCandidate {
  subscriptionId: string;
  productCode: string;
  productName: string;
  planName: string | null;
  estimatedMonthlySaving: number;
}

interface CommitmentPayload {
  terms: CommitmentTerms;
  active: CommitmentView[];
  candidates: CommitmentCandidate[];
}

const won = (n: number) => `${Math.round(n).toLocaleString('ko-KR')}원`;
const pct = (rate: number) => `${Math.round(rate * 100)}%`;

export function CommitmentPanel() {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();

  const data = useQuery<CommitmentPayload, NormalizedError>({
    queryKey: ['billing', 'commitments'],
    queryFn: async () => (await apiClient.get<CommitmentPayload>('/billing/commitments')).data,
  });

  const start = useMutation({
    mutationFn: async (subscriptionId: string) =>
      (await apiClient.post(`/billing/commitments/${subscriptionId}`, {})).data,
    onSuccess: async () => {
      message.success('약정 할인이 적용되었습니다. 다음 청구서부터 깎인 금액으로 나갑니다.');
      await client.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (err: NormalizedError) => message.error(errorText(err)),
  });

  const d = data.data;
  if (data.isLoading) return <Card size="small" loading />;
  if (!d) return null;

  const { months, discountRate } = d.terms;

  const confirmStart = (c: CommitmentCandidate) => {
    Modal.confirm({
      title: `${c.productName} 을(를) ${months}개월 약정하시겠습니까?`,
      icon: <ExclamationCircleFilled style={{ color: '#52c41a' }} />,
      width: 560,
      okText: `${pct(discountRate)} 할인 받기`,
      cancelText: '그만두기',
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.9 }}>
          <div>
            · 오늘부터 <b>{months}개월</b> 동안 이 제품 청구액의{' '}
            <b>{pct(discountRate)}</b> 를 깎아 드립니다
          </div>
          <div>
            · 지금 수량 기준으로 한 달에 약 <b>{won(c.estimatedMonthlySaving)}</b> 아끼십니다
          </div>
          <div>
            · 약정 기간을 채우기 전에 그만두시면,{' '}
            <b>그때까지 받으신 할인액을 마지막 청구서에 되돌려</b> 붙입니다
          </div>
          <div>· 따로 떼는 위약금은 없습니다 — 약정하지 않은 것과 같아질 뿐입니다</div>
          <div>· 사람 수는 약정 중에도 자유롭게 늘리고 줄이실 수 있습니다</div>
        </div>
      ),
      onOk: () => start.mutateAsync(c.subscriptionId).then(() => undefined),
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message={`${months}개월 약정하시면 ${pct(discountRate)} 깎아 드립니다`}
        description={
          <span style={{ fontSize: 12 }}>
            할인은 <b>그 제품 청구액 전체</b>(정기 요금·사람 수·초과 사용분)에 걸립니다.
            약정 중에도 사람 수는 자유롭게 늘리고 줄이실 수 있고, 중도에 그만두시면 그때까지
            받으신 할인액만 되돌려 내시면 됩니다. <b>따로 떼는 위약금은 없습니다.</b>
          </span>
        }
      />

      {d.active.length === 0 && d.candidates.length === 0 && (
        <Card size="small">
          <Empty description="약정을 걸 수 있는 유료 구독이 없습니다." />
        </Card>
      )}

      {/* 걸려 있는 약정 */}
      {d.active.map((c) => (
        <Card
          key={c.commitmentId}
          size="small"
          title={
            <Space>
              <strong>{c.productName}</strong>
              <Tag color="green">{c.months}개월 약정 · {pct(c.discountRate)} 할인</Tag>
            </Space>
          }
        >
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="약정 기간">
              {c.startedOn} ~ {c.endsOn}
            </Descriptions.Item>
            <Descriptions.Item label="요금제">{c.planName ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="지금까지 아끼신 금액">
              <Text strong style={{ color: '#389e0d' }}>
                {won(c.savedSoFar)}
              </Text>
            </Descriptions.Item>
            {/*
              여기가 이 카드의 핵심이다. 아낀 금액만 적어 두면 「공짜로 받은
              것」으로 읽히고, 그만두는 날 환수 금액을 처음 보게 된다.
            */}
            <Descriptions.Item label="지금 그만두시면">
              <Text strong style={{ color: c.clawbackIfBrokenNow > 0 ? '#cf1322' : undefined }}>
                {won(c.clawbackIfBrokenNow)} 반환
              </Text>
            </Descriptions.Item>
          </Descriptions>
          <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 8, marginBottom: 0 }}>
            약정 기간을 채우시면 반환할 금액이 없습니다. 기간이 끝나면 할인도 함께 끝나며,
            그때 다시 약정하실 수 있습니다. 약정을 중간에 푸는 길은 서비스 종료뿐이고, 그때
            위 금액이 마지막 청구서에 함께 붙습니다.
          </Paragraph>
        </Card>
      ))}

      {/* 걸 수 있는 구독 */}
      {d.candidates.map((c) => (
        <Card
          key={c.subscriptionId}
          size="small"
          title={<strong>{c.productName}</strong>}
          extra={
            <Button
              type="primary"
              size="small"
              loading={start.isPending}
              onClick={() => confirmStart(c)}
            >
              {months}개월 약정하기
            </Button>
          }
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            {c.planName ? `${c.planName} · ` : ''}
            지금 수량 기준으로 한 달에 약 <b>{won(c.estimatedMonthlySaving)}</b>,{' '}
            {months}개월이면 약 <b>{won(c.estimatedMonthlySaving * months)}</b> 아끼십니다.
            (실제 금액은 그달 사람 수와 사용량에 따라 달라집니다)
          </Text>
        </Card>
      ))}
    </Space>
  );
}
